/*
 * Landing screen — the prototype's hero + value sections + stages
 * grid + method blurb. Copy is verbatim. The "Start over" path on the
 * results page also brings the buyer back here.
 */
import type { ReactNode } from 'react';
import { usePlanBuilderConfigContext } from '../PlanBuilderConfigContext';

interface Props {
  onStart(): void;
  onOpenTeamModal(): void;
}

export function Landing({ onStart, onOpenTeamModal }: Props) {
  const config = usePlanBuilderConfigContext();
  const { landing } = config.content;

  return (
    <>
      <div className="pb-hero">
        <div className="pb-hero-badge">{landing.heroBadge}</div>
        <h1 dangerouslySetInnerHTML={{ __html: landing.heroTitle }} />
        <p>
          {landing.heroSub}
        </p>
        <button className="pb-btn-primary" onClick={onStart}>
          {landing.ctaPrimary}
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
               stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
        <div className="pb-hero-meta">{landing.meta}</div>
        <div className="pb-hero-team-cta">
          Running this with your whole team?{' '}
          <button type="button" onClick={onOpenTeamModal}>Set up a team space →</button>
        </div>
      </div>

      <div className="pb-stats-bar">
        <div className="pb-stat-item">
          <div className="pb-stat-num">14</div>
          <div className="pb-stat-label">evidence-grounded questions across three dimensions</div>
        </div>
        <div className="pb-stat-item">
          <div className="pb-stat-num">4</div>
          <div className="pb-stat-label">operational maturity stages — Reactive to Strategic</div>
        </div>
        <div className="pb-stat-item">
          <div className="pb-stat-num">6</div>
          <div className="pb-stat-label">IHI Joy-in-Work drivers profiled for your team</div>
        </div>
        <div className="pb-stat-item">
          <div className="pb-stat-num">1</div>
          <div className="pb-stat-label">pre-mortem revealing your most likely failure mode</div>
        </div>
      </div>

      <div className="pb-value-section">
        <div className="pb-value-inner">
          <h2>What your plan includes</h2>
          <p className="pb-value-sub">
            Four components, all grounded in research. Together they give you
            a change you can actually make — and a reason to trust it'll deliver.
          </p>
          <div className="pb-value-grid">
            {landing.howItWorks.map((card) => (
              <ValueCard
                key={card.title}
                title={card.title}
                body={card.body}
                icon={cardIcon(card.title)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="pb-stages-section">
        <div className="pb-stages-inner">
          <h2>Your starting point matters</h2>
          <p className="pb-stages-sub">
            Almost every legal team moves through these four stages. The same
            advice doesn't work at all of them — so we place you precisely,
            then build the plan from there.
          </p>
          <div className="pb-stages-grid">
            <StageCard n={1} label={config.scoring.stages[1].name} body="Work arrives informally. Priorities shift daily. Running on goodwill and heroics." />
            <StageCard n={2} label={config.scoring.stages[2].name} body="Basic structure exists but fragmented. Busy, but not yet visible." />
            <StageCard n={3} label={config.scoring.stages[3].name} body="Demand is managed. Capacity is clear. Trusted but not yet strategic." />
            <StageCard n={4} label={config.scoring.stages[4].name} body="High-trust, high-visibility partner. Funded because ROI is proven." />
          </div>
        </div>
      </div>

      <div className="pb-method-section">
        <div className="pb-method-inner">
          <strong>Designed to deliver, not just to diagnose.</strong> Heath's <em>Reset</em> methodology (2025) is explicit: meaningful
          change comes from a small, well-placed move — not a big programme. We
          pair that with Klein's Pre-Mortem technique (HBR, 2007) to surface
          the failure mode before it happens, the IHI Framework for Improving
          Joy in Work (Perlo et al., 2017) to pinpoint where pressure is
          concentrated, and Pfeffer &amp; Sutton's evidence-based management
          principles to keep every recommendation grounded. No opinions. No
          strings attached. An evidence-based plan specifically for your team.
        </div>
      </div>

      <div className="pb-landing-cta">
        <h2>Ready to build your plan?</h2>
        <p>Under 10 minutes. Answers save automatically. Come back any time.</p>
        <button className="pb-btn-primary" onClick={onStart}>
          {landing.ctaPrimary}
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
               stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </>
  );
}

// Static icon lookup by card title — icons are structural, not copy.
function cardIcon(title: string): ReactNode {
  switch (title) {
    case 'Your starting point':
      return (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24"
             stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    case "What's draining your team":
      return (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24"
             stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
        </svg>
      );
    case 'Two moves to make first':
      return (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24"
             stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      );
    default:
      return (
        <svg width="22" height="22" fill="none" viewBox="0 0 24 24"
             stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008v.008H12v-.008z" />
        </svg>
      );
  }
}

function ValueCard({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="pb-value-card">
      <div className="pb-value-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

function StageCard({ n, label, body }: { n: 1 | 2 | 3 | 4; label: string; body: string }) {
  return (
    <div className={`pb-stage-card s${n}`}>
      <div className="pb-stage-num">Stage {n}</div>
      <div className="pb-stage-name">{label}</div>
      <div className="pb-stage-desc">{body}</div>
    </div>
  );
}
